import React from 'react';
import { Pressable, TextInput } from 'react-native';
import { Image } from 'expo-image';

import { Box, Text, useTheme } from 'app/shopify/theme';

type TextFieldSize = 'default' | 'small';
type TextFieldState = 'enabled' | 'hover' | 'active' | 'focus' | 'disabled' | 'readOnly';
type TextFieldValidation = 'none' | 'error';

export interface TextFieldProps {
  label?: string;
  value?: string;
  placeholder?: string;
  helperText?: string;
  errorMessage?: string;
  size?: TextFieldSize;
  state?: TextFieldState;
  validation?: TextFieldValidation;
  leadingIcon?: React.ReactNode;
  showInfoIcon?: boolean;
  showClearButton?: boolean;
  prefix?: string;
  onChangeText?: (value: string) => void;
  onClear?: () => void;
}

interface PressableInteractionState {
  pressed: boolean;
  hovered?: boolean;
}

const ICON_INFO_24 = 'https://www.figma.com/api/mcp/asset/1ea1a986-8bc5-4775-bc94-577da5baae3b';
const ICON_INFO_20 = 'https://www.figma.com/api/mcp/asset/05c5c016-54ff-41b8-b2ad-749f6944fa9c';
const ICON_CLEAR_24 = 'https://www.figma.com/api/mcp/asset/9789cfe5-30ca-45e4-94f2-5fc6b7148b81';
const ICON_CLEAR_20 = 'https://www.figma.com/api/mcp/asset/20734540-01a1-4430-989a-de8e4ef27110';
const ICON_LOCK_24 = 'https://www.figma.com/api/mcp/asset/d9a1983a-6e15-4807-a7e4-7a7964e97c7d';
const ICON_ERROR_17 = 'https://www.figma.com/api/mcp/asset/d825b382-f3e3-4e1b-a211-c5bc932381fd';

function getNumericToken(value: string | undefined, fallback: number) {
  if (!value) return fallback;
  const n = Number(value.replace(',', '.'));
  return Number.isFinite(n) ? n : fallback;
}

export function TextField({
  label = 'Label',
  value,
  placeholder = 'Placeholder',
  helperText = 'Helper text',
  errorMessage = 'Error message',
  size = 'default',
  state = 'enabled',
  validation = 'none',
  leadingIcon,
  showInfoIcon = false,
  showClearButton = false,
  prefix,
  onChangeText,
  onClear,
}: TextFieldProps) {
  const theme = useTheme();
  const isSmall = size === 'small';
  const isDisabled = state === 'disabled';
  const isReadOnly = state === 'readOnly';
  const isError = validation === 'error';
  const hasValue = (value ?? '').length > 0;

  const fieldHeight = isSmall ? 40 : 48;
  const labelVariant = 'bodySmallMedium';
  const helperVariant = 'bodySmall';
  const inputVariant = 'bodyBase';

  const isFrozenState = state !== 'enabled';
  const showFocusRing = state === 'focus';

  const borderWidthSubtle = getNumericToken(theme.tokens.borderWidth['border-width.subtle'], 1);
  const borderWidthStandard = getNumericToken(theme.tokens.borderWidth['border-width.standard'], 1.5);
  const borderWidthEmphasized = getNumericToken(theme.tokens.borderWidth['border-width.emphasized'], 2);

  function getBorderColor(pressed: boolean, hovered: boolean) {
    if (isError) return theme.colors['border.error'];
    if (state === 'active' || pressed) return theme.colors['border.brand'];
    if (state === 'hover' || hovered) return theme.colors['border.interactive'];
    if (isDisabled) return theme.colors['border.disabled'];
    return theme.colors['border.secondary'];
  }

  function getBackgroundColor() {
    if (isDisabled) return theme.colors['surface.disabled'];
    return theme.colors['surface.primary-neutral'];
  }

  function getLabelColor() {
    if (isDisabled) return theme.colors['text.disabled'];
    return isError ? theme.colors['text.error'] : theme.colors['text.secondary'];
  }

  function getInputColor() {
    if (isDisabled) return theme.colors['text.disabled'];
    if (!hasValue) return theme.colors['text.placeholder'];
    return theme.colors['text.primary'];
  }

  function renderTrailingIcon(uri: string, iconSize: number, wrapperSize: number, disabledOpacity = false) {
    return (
      <Box
        alignItems="center"
        justifyContent="center"
        width={wrapperSize}
        height={wrapperSize}
        borderRadius="radius.full"
        style={{ opacity: disabledOpacity ? 0.4 : 1 }}
      >
        <Image source={{ uri }} style={{ width: iconSize, height: iconSize }} />
      </Box>
    );
  }

  function renderInner(interaction?: PressableInteractionState) {
    const hovered = interaction?.hovered ?? false;
    const pressed = interaction?.pressed ?? false;
    const resolvedHovered = isFrozenState ? false : hovered && !pressed;
    const resolvedPressed = isFrozenState ? false : pressed;

    const borderColor = getBorderColor(resolvedPressed, resolvedHovered);
    const borderWidth = isError || resolvedPressed || state === 'active' ? borderWidthStandard : borderWidthSubtle;

    const shouldShowClear = showClearButton && !isReadOnly;
    const iconSize = isSmall ? 20 : 24;
    const iconWrapperSize = isSmall ? 32 : 40;

    return (
      <Box gap="spacing.general.x1" width="100%">
        <Text variant={labelVariant} style={{ color: getLabelColor() }}>
          {label}
        </Text>

        <Box
          position="relative"
          height={fieldHeight}
          borderRadius="radius.full"
          borderWidth={borderWidth}
          paddingHorizontal="spacing.general.x1"
          flexDirection="row"
          alignItems="center"
          style={{
            backgroundColor: getBackgroundColor(),
            borderColor,
          }}
        >
          <Box
            flex={1}
            minHeight={24}
            flexDirection="row"
            alignItems="center"
            paddingLeft={isSmall ? 'spacing.general.x2' : 'spacing.general.x3'}
            gap="spacing.general.x1"
          >
            {leadingIcon ? (
              <Box width={iconSize} height={iconSize} alignItems="center" justifyContent="center">
                {leadingIcon}
              </Box>
            ) : null}

            <Box flex={1} minWidth={1} flexDirection="row" alignItems="center" paddingHorizontal="spacing.general.x1">
              {prefix ? (
                <Text
                  variant={inputVariant}
                  numberOfLines={1}
                  style={{ color: isDisabled ? theme.colors['text.disabled'] : theme.colors['text.primary'] }}
                >
                  {prefix}
                </Text>
              ) : null}

              <TextInput
                value={value ?? ''}
                editable={!isDisabled && !isReadOnly}
                placeholder={placeholder}
                placeholderTextColor={theme.colors['text.placeholder']}
                onChangeText={onChangeText}
                style={{
                  flex: 1,
                  minWidth: 1,
                  color: getInputColor(),
                  fontFamily: theme.textVariants[inputVariant].fontFamily,
                  fontSize: theme.textVariants[inputVariant].fontSize,
                  fontWeight: theme.textVariants[inputVariant].fontWeight,
                  letterSpacing: theme.textVariants[inputVariant].letterSpacing,
                  lineHeight: theme.textVariants[inputVariant].lineHeight,
                  paddingVertical: 0,
                }}
              />
            </Box>
          </Box>

          {shouldShowClear ? (
            <Pressable onPress={onClear} disabled={isDisabled}>
              {renderTrailingIcon(isSmall ? ICON_CLEAR_20 : ICON_CLEAR_24, iconSize, iconWrapperSize, isDisabled)}
            </Pressable>
          ) : null}

          {showInfoIcon
            ? renderTrailingIcon(isSmall ? ICON_INFO_20 : ICON_INFO_24, iconSize, iconWrapperSize, isDisabled)
            : null}

          {isReadOnly ? (
            <Box width={40} height={40} alignItems="center" justifyContent="center">
              <Image source={{ uri: ICON_LOCK_24 }} style={{ width: 24, height: 24 }} />
            </Box>
          ) : null}

          {showFocusRing ? (
            <Box
              position="absolute"
              top={-5}
              left={-5}
              right={-5}
              bottom={-5}
              borderRadius="radius.full"
              borderWidth={borderWidthEmphasized}
              borderColor="border.focused"
            />
          ) : null}
        </Box>

        {isError ? (
          <Box flexDirection="row" alignItems="center" gap="spacing.general.x1">
            <Image source={{ uri: ICON_ERROR_17 }} style={{ width: 17, height: 17 }} />
            <Text variant={helperVariant} style={{ color: theme.colors['text.error'] }}>
              {errorMessage}
            </Text>
          </Box>
        ) : (
          <Text
            variant={helperVariant}
            style={{ color: isDisabled ? theme.colors['text.disabled'] : theme.colors['text.secondary'] }}
          >
            {helperText}
          </Text>
        )}
      </Box>
    );
  }

  if (!isFrozenState) {
    return (
      <Pressable>
        {(pressableState) =>
          renderInner({
            pressed: pressableState.pressed,
            hovered: 'hovered' in pressableState ? (pressableState as PressableInteractionState).hovered : false,
          })
        }
      </Pressable>
    );
  }

  return <Box>{renderInner()}</Box>;
}
